import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

const CANONICAL_BASE_URL = 'https://www.oteudinheiro.com.br';

const normalizeCanonicalUrl = (rawUrl?: string): string => {
  if (!rawUrl && typeof window !== 'undefined') {
    const cleanPath = window.location.pathname === '/' ? '' : window.location.pathname.replace(/\/+$/, '');
    return `${CANONICAL_BASE_URL}${cleanPath}`;
  }
  if (!rawUrl) return CANONICAL_BASE_URL;
  return rawUrl
    .replace(/^https?:\/\/(www\.)?oteudinheiro\.com\.br/, CANONICAL_BASE_URL)
    .replace(/\/+$/, '') || CANONICAL_BASE_URL;
};

export const useSEO = ({
  title = 'O Teu Dinheiro - Finanças Pessoais, Investimentos e Calculadoras (2026)',
  description = 'Educação financeira simples e prática para o brasileiro. Calculadoras gratuitas, comparativos de investimentos, cartões, PIX, planilhas e economia.',
  keywords = 'finanças pessoais, educação financeira, investimentos, economia, PIX, cartão de crédito, dívidas, Brasil',
  image = typeof window !== 'undefined' && window.location.pathname.startsWith('/blog/')
    ? `${CANONICAL_BASE_URL}/og/${window.location.pathname.split('/blog/')[1]}.png`
    : `${CANONICAL_BASE_URL}/logo512.png`,
  url = typeof window !== 'undefined' ? window.location.href : CANONICAL_BASE_URL,
  type = 'website',
  author = 'O Teu Dinheiro',
  publishedTime,
  modifiedTime,
  section,
  tags = []
}: SEOProps = {}) => {
  useEffect(() => {
    const canonicalUrl = normalizeCanonicalUrl(url);

    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('language', 'pt-BR');
    updateMetaTag('revisit-after', '7 days');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'O Teu Dinheiro', true);
    updateMetaTag('og:locale', 'pt_BR', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:site', '@oteudinheiro');
    updateMetaTag('twitter:creator', '@oteudinheiro');

    // Clear previous article:tag elements before adding new ones
    document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());

    // Article specific tags
    if (type === 'article') {
      if (publishedTime) updateMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) updateMetaTag('article:modified_time', modifiedTime, true);
      if (author) updateMetaTag('article:author', author, true);
      if (section) updateMetaTag('article:section', section, true);
      tags.forEach(tag => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'article:tag');
        meta.content = tag;
        document.head.appendChild(meta);
      });
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

  }, [title, description, keywords, image, url, type, author, publishedTime, modifiedTime, section, tags]);
};
